import {
  bumpSandboxPoll,
  buildSandboxSnapshot,
  codeFromSandboxToken,
  isSandboxCode,
  markSandboxPreview,
  markSandboxSubmit,
  parseSandboxCode,
  SANDBOX_EMAIL,
  type SandboxSnapshot,
} from './scenarios'

export const SANDBOX_FILL_EVENT = 'cdk-sandbox-fill'

export type SandboxFillDetail = {
  code: string
}

function jsonResponse(status: number, body: unknown) {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  })
}

function requestUrl(input: RequestInfo | URL) {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

function parseApi(url: string) {
  try {
    const u = new URL(url, 'http://sandbox.local')
    return { path: u.pathname, query: u.searchParams }
  } catch {
    return { path: '', query: new URLSearchParams() }
  }
}

async function readBodyCode(init?: RequestInit) {
  const raw = init?.body
  if (!raw || typeof raw !== 'string') return ''
  try {
    const body = JSON.parse(raw) as { code?: string; redemption_token?: string }
    if (body.code) return String(body.code)
    if (body.redemption_token) return codeFromSandboxToken(String(body.redemption_token))
  } catch {
    /* ignore */
  }
  return ''
}

function resultPayload(snap: SandboxSnapshot) {
  return {
    ...snap.result,
    order: {
      status: snap.result.status,
      stage: snap.result.stage,
      message: snap.result.message,
      account_email: snap.result.account_email,
    },
  }
}

export async function handleSandboxFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response | null> {
  const url = requestUrl(input)
  if (!url.includes('/api/v1/')) return null
  const { path, query } = parseApi(url)
  const method = String(init?.method || 'GET').toUpperCase()

  if (path === '/api/v1/lookup/cdk') {
    const code = query.get('code') || ''
    if (!isSandboxCode(code)) return null
    const snap = buildSandboxSnapshot({ code })
    if (!snap) return null
    return jsonResponse(200, snap.lookup)
  }

  if (path === '/api/v1/public/cdk/result-by-code') {
    const code = query.get('code') || query.get('cdk_code') || ''
    if (!isSandboxCode(code)) return null
    bumpSandboxPoll(code)
    const snap = buildSandboxSnapshot({ code })
    if (!snap) return null
    return jsonResponse(snap.resultHttp, resultPayload(snap))
  }

  if (path === '/api/v1/public/cdk/result') {
    const token = query.get('token') || ''
    const code = codeFromSandboxToken(token)
    if (!isSandboxCode(code)) return null
    bumpSandboxPoll(code)
    const snap = buildSandboxSnapshot({ code })
    if (!snap) return null
    return jsonResponse(snap.resultHttp, resultPayload(snap))
  }

  if (path === '/api/v1/public/cdk/preview' && method === 'POST') {
    const code = await readBodyCode(init)
    if (!isSandboxCode(code)) return null
    markSandboxPreview(code)
    const snap = buildSandboxSnapshot({ code })
    if (!snap) return null
    return jsonResponse(snap.previewHttp, snap.preview)
  }

  if (path === '/api/v1/public/cdk/account-check' && method === 'POST') {
    const code = await readBodyCode(init)
    if (!isSandboxCode(code)) return null
    const scenario = parseSandboxCode(code)
    if (!scenario) return null
    if (scenario.id === 'account-switch') {
      return jsonResponse(422, {
        notice: 'failed',
        error: '账号异常，请更换账号后重试',
        message: '账号异常，请更换账号后重试',
      })
    }
    if (scenario.id === 'account-busy') {
      return jsonResponse(422, {
        notice: 'account_busy',
        error: '该账号已有开通中的订单',
        message: '该账号已有开通中的订单',
      })
    }
    if (scenario.id === 'satisfied') {
      return jsonResponse(200, {
        currentPlan: 'plus',
        current_plan: 'plus',
        email: SANDBOX_EMAIL,
        can_open: false,
        subscription_has_active: true,
        target_plan: scenario.plan,
      })
    }
    return jsonResponse(200, {
      currentPlan: 'free',
      current_plan: 'free',
      email: SANDBOX_EMAIL,
      can_open: true,
      subscription_has_active: false,
      target_plan: scenario.plan,
    })
  }

  if (path === '/api/v1/public/cdk/submit' && method === 'POST') {
    const code = await readBodyCode(init)
    if (!isSandboxCode(code)) return null
    markSandboxSubmit(code)
    const snap = buildSandboxSnapshot({ code })
    if (!snap) return null
    if (snap.submit.delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, snap.submit.delayMs))
    }
    const again = buildSandboxSnapshot({ code }) || snap
    return jsonResponse(again.submit.http, again.submit.body)
  }

  if ((path === '/api/v1/public/cdk/preflight' || path === '/api/v1/public/cdk/redeem') && method === 'POST') {
    const code = await readBodyCode(init)
    const scenario = parseSandboxCode(code)
    if (!scenario) return null
    return jsonResponse(200, {
      status: 'processing',
      message: '沙箱请走「立即兑换」一键提交，不要拆预检/兑换。',
      redemption_token: scenario ? `pub-sb-${scenario.code}` : '',
    })
  }

  return null
}
